import React, { useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Phone, Menu, X, MessageCircle, CalendarDays } from "lucide-react";

const NAV = [
  { to: "/angebot?type=full", label: "Kompletter Umzug", section: null },
  { to: "/angebot?type=small", label: "Kleine Transporte", section: null },
  { to: "/entruempelung", label: "Entrümpelung", section: null },
  { to: "/#how", label: "So funktioniert's", section: "how" },
  { to: "/#preise", label: "Preise", section: "preise" },
  { to: "/#faq", label: "FAQ", section: "faq" },
];

function scrollToHash(hash) {
  if (!hash) return;
  const id = hash.replace("#", "");
  requestAnimationFrame(() => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  });
}

export default function SiteHeader() {
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();

  const handleNav = (item) => (e) => {
    setOpen(false);
    if (item.section && pathname === "/") {
      e.preventDefault();
      scrollToHash("#" + item.section);
    }
  };

  return (
    <header className="sticky top-0 z-40 w-full backdrop-blur-md bg-white/85 border-b border-slate-200" data-testid="site-header">
      <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8 h-16 flex items-center justify-between gap-3">
        <Link to="/" className="flex items-center gap-2.5 shrink-0" data-testid="brand-link">
          <img
            src="/leukas-logo.png"
            alt="Leukas Umzug Service Logo"
            width="640"
            height="641"
            className="h-14 w-auto object-contain rounded-lg"
            data-testid="brand-logo"
          />
          <div className="leading-tight">
            <div className="font-heading font-bold text-slate-900 text-[15px]">Leukas Umzug</div>
            <div className="text-[10px] uppercase tracking-widest text-slate-500">Service · Aachen · Köln · Düsseldorf</div>
          </div>
        </Link>

        {/* Desktop nav */}
        <nav className="hidden lg:flex items-center gap-1" data-testid="nav-desktop">
          {NAV.map((item) => (
            <NavLink
              key={item.label}
              to={item.to}
              onClick={handleNav(item)}
              className={({ isActive }) =>
                `px-3 py-2 text-sm font-semibold rounded-lg transition-colors ${
                  isActive && item.to === "/" ? "text-slate-900" : "text-slate-600 hover:text-slate-900 hover:bg-slate-100"
                }`
              }
              data-testid={`nav-${item.label}`}
            >
              {item.label}
            </NavLink>
          ))}
        </nav>

        {/* Right cluster */}
        <div className="flex items-center gap-2">
          <a
            href="https://wa.me/4917656198464"
            target="_blank"
            rel="noreferrer"
            className="hidden md:inline-flex items-center gap-1.5 text-[13px] font-semibold text-slate-700 hover:text-brand-navy transition-colors"
            data-testid="header-phone"
          >
            <Phone size={14} /> +49 176 56198464
          </a>
          <Link
            to="/angebot?type=full"
            className="hidden sm:inline-flex items-center gap-1.5 rounded-xl bg-brand-amber text-brand-navy px-3.5 py-2 text-sm font-bold hover:bg-amber-300 transition-colors"
            data-testid="header-cta"
          >
            Angebot erhalten
          </Link>
          <button
            type="button"
            onClick={() => setOpen((v) => !v)}
            className="lg:hidden w-10 h-10 grid place-items-center rounded-xl border border-slate-200 hover:bg-slate-50 transition-colors"
            aria-label="Menü"
            data-testid="nav-toggle"
          >
            {open ? <X size={18} /> : <Menu size={18} />}
          </button>
        </div>
      </div>

      {/* Mobile menu */}
      {open && (
        <div className="lg:hidden border-t border-slate-200 bg-white" data-testid="nav-mobile">
          <div className="max-w-7xl mx-auto px-4 py-3 space-y-1">
            {NAV.map((item) => (
              <Link
                key={item.label}
                to={item.to}
                onClick={handleNav(item)}
                className="block px-3 py-2.5 text-[15px] font-semibold text-slate-700 rounded-lg hover:bg-slate-100 hover:text-slate-900 transition-colors"
                data-testid={`mob-nav-${item.label}`}
              >
                {item.label}
              </Link>
            ))}
            <div className="h-px bg-slate-100 my-2" />
            <a
              href="https://wa.me/4917656198464"
              target="_blank"
              rel="noreferrer"
              className="flex items-center gap-2 px-3 py-2.5 text-[15px] font-semibold text-green-700 rounded-lg hover:bg-green-50 transition-colors"
            >
              <MessageCircle size={16} /> WhatsApp · +49 176 56198464
            </a>
            <a
              href="/admin/login"
              target="_blank"
              rel="noreferrer"
              className="flex items-center gap-2 px-3 py-2.5 text-[13px] font-semibold text-slate-500 rounded-lg hover:bg-slate-100 transition-colors"
              data-testid="mob-admin"
            >
              <CalendarDays size={14} /> Admin Kalender
            </a>
            <Link
              to="/angebot?type=full"
              onClick={() => setOpen(false)}
              className="block mt-2 text-center rounded-xl bg-brand-navy text-white px-3.5 py-3 text-sm font-bold hover:bg-slate-800 transition-colors"
            >
              Angebot erhalten
            </Link>
          </div>
        </div>
      )}
    </header>
  );
}
